// Shared application data boundary. Preview behavior is injected only into design HTML.
import { useCallback, useEffect, useState } from 'react';
import seed from './projectDataSeed.json';
import schema from './projectDataSchema.json';

const listeners = new Map();
const adapter = () => typeof window !== 'undefined' ? window.__8080PreviewData : null;
const notify = (key) => (listeners.get(key) || new Set()).forEach((listener) => listener());
const getAuthToken = () => {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage.getItem('auth_token') : null;
  } catch {
    return null;
  }
};

async function request(key, method = 'GET', id, body) {
  if (!/^[a-zA-Z][a-zA-Z0-9_-]{0,79}$/.test(key)) throw new Error('Invalid data collection');
  const preview = adapter();
  if (preview) return preview.request(key, method, id, body, seed[key]);
  const token = getAuthToken();
  const headers = {
    ...(body === undefined ? null : { 'Content-Type': 'application/json' }),
    ...(token ? { Authorization: `Bearer ${token}` } : null),
  };
  const response = await fetch(`/api/${encodeURIComponent(key)}${id == null ? '' : `/${encodeURIComponent(id)}`}`, {
    method,
    headers: Object.keys(headers).length ? headers : undefined,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`Could not ${method === 'GET' ? 'load' : 'save'} ${key} (${response.status})`);
  const result = response.status === 204 ? null : await response.json();
  if (method === 'GET' && !Array.isArray(result)) throw new Error(`Expected a collection from /api/${key}`);
  if (method !== 'GET') notify(key);
  return result;
}

export const projectData = {
  schema,
  list: (key) => request(key),
  create: (key, record) => request(key, 'POST', undefined, record),
  update: (key, id, patch) => request(key, 'PATCH', id, patch),
  remove: (key, id) => request(key, 'DELETE', id),
  subscribe(key, listener) {
    const preview = adapter();
    if (preview) return preview.subscribe(key, listener);
    if (!listeners.has(key)) listeners.set(key, new Set());
    listeners.get(key).add(listener);
    return () => listeners.get(key)?.delete(listener);
  },
};

export function useProjectCollection(key) {
  const [state, setState] = useState({ data: [], loading: true, error: null });
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  useEffect(() => projectData.subscribe(key, refresh), [key, refresh]);
  useEffect(() => {
    let active = true;
    setState((value) => ({ ...value, loading: true, error: null }));
    projectData.list(key).then(
      (data) => { if (active) setState({ data, loading: false, error: null }); },
      (error) => { if (active) setState((value) => ({ ...value, loading: false, error: error.message || String(error) })); },
    );
    return () => { active = false; };
  }, [key, revision]);
  return { ...state, refresh };
}
